import { useTranslation } from 'react-i18next';
import { formatDateTime, formatRelative } from '../utils/format';
import { isReminder } from '../utils/reminderModel';
import type { Notification } from '../types';
import { isUnread, occurredAt } from './notificationCenterModel';
import { KIND_ICON, describeNotification, notificationKind, notificationTone } from './notificationView';

const TONE_CLASS = { neutral: 'text-text-3', warning: 'text-warning-600', danger: 'text-danger-600' } as const;

/**
 * FIX-NOTIF N-1 - the ONE notification row: the drawer's list and the portal
 * overview's "recent notifications" both draw it. A type icon (grey; coloured
 * only for a debt, an approval or a delay - Q1), the one-line title, relative
 * time, the subject, a preview (comments) and the quiet context line.
 * The caller owns what a click does.
 */
export default function NotificationRow({ n, onOpen }: { n: Notification; onOpen: () => void }) {
  const { t } = useTranslation();
  const preview = n.event_type === 'comment_added' ? n.metadata?.message_preview : undefined;
  // The ticket's subject gets its own line ("קריאה חדשה נפתחה" alone says too little);
  // the customer stays in the quiet context line. A ticket that no longer exists says so.
  const meta = (n.metadata ?? {}) as Record<string, string | undefined>;
  // DQ-381: a meeting notice names the meeting and its time ("הוזמנת לפגישה" alone says too little).
  const meetingLine = n.event_type.startsWith('meeting_') && typeof n.metadata?.title === 'string' && n.metadata.title
    ? `${n.metadata.title}${n.metadata?.meeting_datetime ? ` · ${formatDateTime(String(n.metadata.meeting_datetime))}` : ''}`
    : '';
  const subject = meta.ticket_title || n.ticket_title || meetingLine
    // DQ-232: non-ticket notices name their milestone / project / contract / bank.
    || meta.title || meta.project_name || meta.engagement_name || meta.bank_name || meta.company_name;
  const ticketGone = !!n.ticket_id && !subject;
  const context = ticketGone ? t('notificationCenter.ticketGone') : (n.organization_name || meta.organization_name || '');
  const repeats = n.occurrence_count ?? 1;
  const fresh = isUnread(n);
  const Icon = KIND_ICON[notificationKind(n.event_type)];
  const tone = notificationTone(n.event_type);
  const when = occurredAt(n);
  return (
    <button
      type="button"
      onClick={onOpen}
      data-notification-row=""
      data-kind={notificationKind(n.event_type)}
      className="flex w-full items-start gap-2.5 py-2.5 pe-5 ps-3 text-start transition-colors duration-micro hover:bg-surface-subtle focus-visible:bg-surface-subtle"
    >
      <span
        aria-hidden="true"
        className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${fresh ? 'bg-brand' : ''}`}
      />
      <Icon size={16} strokeWidth={1.75} aria-hidden="true" className={`mt-0.5 flex-shrink-0 ${TONE_CLASS[tone]}`} />
      <span className="block min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-3">
          <span className={`min-w-0 truncate text-sm ${fresh ? 'font-medium text-text' : 'text-text-2'}`}>
            {fresh && <span className="sr-only">{t('notificationCenter.unreadMark')} </span>}
            <bdi>{describeNotification(n, t)}</bdi>
            {repeats > 1 && (
              <span className="ms-1.5 text-xs font-normal text-text-2">{t('notifications.repeated', { count: repeats })}</span>
            )}
          </span>
          <time
            dateTime={when.toISOString()}
            className="flex-shrink-0 whitespace-nowrap text-xs text-text-2"
          >
            {formatRelative(when)}
          </time>
        </span>
        {isReminder(n) && n.metadata?.is_self === false && n.metadata?.creator_name && (
          <span className="mt-0.5 block truncate text-xs text-text-2"><bdi>{t('reminder.fromCreator', { name: n.metadata.creator_name })}</bdi></span>
        )}
        {subject && <span className="mt-0.5 line-clamp-2 block text-sm text-text"><bdi>{subject}</bdi></span>}
        {preview && <span className="mt-0.5 line-clamp-2 block text-sm text-text-2"><bdi>{preview}</bdi></span>}
        {context && <span className="mt-0.5 block truncate text-xs text-text-2"><bdi>{context}</bdi></span>}
      </span>
    </button>
  );
}
